import { useMemo, useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { format } from "date-fns";
import {
  ArrowRight,
  ChevronDown,
  Compass,
  Layers,
  Route as RouteIcon,
  Workflow,
} from "lucide-react";

import { useMeetingBySlug } from "@/application/use-cases/meetings/use-meetings.queries";
import { MermaidDiagram } from "@/presentation/components/diagrams/mermaid-diagram";
import { PageHeader } from "@/presentation/components/shell/page-header";
import { RouteHero } from "@/presentation/components/shell/route-hero";
import { statusToTone, StatusDot } from "@/presentation/components/status/status-dot";
import type {
  DecisionRow,
  FeatureRow,
  MeetingFlowStep,
  OpenQuestionRow,
} from "@/infrastructure/supabase/database.types";

export const Route = createFileRoute("/_app/discoveries/$slug")({
  component: DiscoveryPage,
});

function DiscoveryPage() {
  const { slug } = Route.useParams();
  const meetingQ = useMeetingBySlug(slug);

  if (meetingQ.isLoading) {
    return (
      <div className="animate-page-enter mx-auto flex w-full max-w-6xl flex-col">
        <PageHeader title="Discovery" />
        <p className="text-muted-foreground px-6 py-8 text-xs italic lg:px-10">Loading…</p>
      </div>
    );
  }

  if (!meetingQ.data) {
    return (
      <div className="animate-page-enter mx-auto flex w-full max-w-6xl flex-col">
        <PageHeader title="Discovery not found" />
        <p className="text-muted-foreground px-6 py-8 text-xs italic lg:px-10">
          No discovery for slug "{slug}".
        </p>
      </div>
    );
  }

  const m = meetingQ.data;
  const openDecisions = m.decisions.filter((d) => d.status === "open");
  const openQuestions = m.openQuestions.filter((q) => q.status === "open");

  return (
    <div className="animate-page-enter mx-auto flex w-full max-w-6xl flex-col gap-12 p-6 lg:gap-16 lg:p-10">
      <RouteHero
        eyebrow="Discovery"
        icon={Compass}
        numeral={String(m.features.length).padStart(2, "0")}
        numeralTone="cyan"
        numeralSuffix="features"
        title={m.title}
        summary={m.summary ?? undefined}
        collapsibleSummary
        meta={[
          { label: "Date", value: format(new Date(m.meeting_at), "EEE d MMM"), tone: "default" },
          { label: "Decisions open", value: openDecisions.length, tone: "amber" },
          { label: "Questions open", value: openQuestions.length, tone: "amber" },
          { label: "Flow steps", value: m.flow_steps.length, tone: "cyan" },
        ]}
        actions={
          <Link
            to="/meetings/view/$slug"
            params={{ slug: m.slug }}
            className="group inline-flex items-center justify-between gap-2 rounded-lg border border-[oklch(1_0_0_/_0.12)] bg-[oklch(1_0_0_/_0.04)] px-4 py-2.5 text-[13px] font-medium text-[oklch(0.93_0.008_240)] transition-all hover:border-[oklch(0.72_0.17_190_/_0.5)] hover:bg-[oklch(0.72_0.17_190_/_0.08)] print:hidden"
          >
            <span className="font-data text-[11px] tracking-[0.12em] uppercase">
              See raw write-up
            </span>
            <ArrowRight className="size-3.5 transition-transform group-hover:translate-x-0.5" />
          </Link>
        }
      />

      {/* Section 1: Features */}
      <DiscoverySection
        eyebrow="01 · Features"
        icon={Layers}
        title="What we'd build"
        intro="The capabilities the room agreed are first-class for this journey. Tap a card to read the description."
      >
        {m.features.length === 0 ? (
          <p className="text-muted-foreground text-xs italic">
            No features were lifted out of this conversation yet.
          </p>
        ) : (
          <div className="grid gap-4 md:grid-cols-2">
            {m.features.map((f) => (
              <FeatureBlock key={f.id} feature={f} />
            ))}
          </div>
        )}
      </DiscoverySection>

      {/* Section 2: User sequence flow */}
      <DiscoverySection
        eyebrow="02 · User sequence flow"
        icon={Workflow}
        title="How an order moves"
        intro="The path through the system as we currently understand it — from the user's intent to the warehouse floor."
      >
        {m.flow_steps.length === 0 ? (
          <p className="text-muted-foreground text-xs italic">
            No sequence flow defined for this discovery yet.
          </p>
        ) : (
          <FlowSequenceDiagram steps={m.flow_steps} />
        )}
      </DiscoverySection>

      {/* Section 3: Open decisions & questions */}
      <DiscoverySection
        eyebrow="03 · Still to decide"
        icon={RouteIcon}
        title="What's still open"
        intro="Decisions in flight and questions without answers. Bring opinions; we'll close these together."
      >
        {openDecisions.length === 0 && openQuestions.length === 0 ? (
          <p className="text-muted-foreground text-xs italic">
            Everything from this meeting has been resolved.
          </p>
        ) : (
          <div className="flex flex-col gap-6">
            {openDecisions.length > 0 ? (
              <div className="flex flex-col gap-3">
                <h3 className="font-data text-muted-foreground text-[10px] tracking-[0.18em] uppercase">
                  Decisions
                </h3>
                <div className="grid gap-3 md:grid-cols-2">
                  {openDecisions.map((d) => (
                    <OpenDecisionBlock key={d.id} decision={d} />
                  ))}
                </div>
              </div>
            ) : null}
            {openQuestions.length > 0 ? (
              <div className="flex flex-col gap-3">
                <h3 className="font-data text-muted-foreground text-[10px] tracking-[0.18em] uppercase">
                  Questions
                </h3>
                <ol className="flex flex-col gap-2">
                  {openQuestions.map((q, idx) => (
                    <OpenQuestionBlock key={q.id} index={idx + 1} question={q} />
                  ))}
                </ol>
              </div>
            ) : null}
          </div>
        )}
      </DiscoverySection>
    </div>
  );
}

// =============================================================================
// Helpers
// =============================================================================

function DiscoverySection({
  eyebrow,
  icon: Icon,
  title,
  intro,
  children,
}: {
  eyebrow: string;
  icon: typeof Workflow;
  title: string;
  intro: string;
  children: React.ReactNode;
}) {
  return (
    <section className="flex flex-col gap-5">
      <div className="flex flex-col gap-2">
        <div className="flex items-center gap-2">
          <Icon className="text-muted-foreground size-4" />
          <span className="eyebrow">{eyebrow}</span>
        </div>
        <h2 className="text-[22px] font-bold tracking-tight">{title}</h2>
        <p className="text-muted-foreground max-w-prose text-[13px] leading-relaxed">{intro}</p>
      </div>
      {children}
    </section>
  );
}

function FeatureBlock({ feature }: { feature: FeatureRow }) {
  const [open, setOpen] = useState(false);
  const hasDescription = !!feature.description;
  return (
    <article
      className="glow-card flex cursor-pointer flex-col gap-3 p-5 transition-shadow hover:shadow-md print:break-inside-avoid"
      onClick={() => hasDescription && setOpen((v) => !v)}
      role={hasDescription ? "button" : undefined}
      aria-expanded={hasDescription ? open : undefined}
      tabIndex={hasDescription ? 0 : undefined}
      onKeyDown={(e) => {
        if (hasDescription && (e.key === "Enter" || e.key === " ")) {
          e.preventDefault();
          setOpen((v) => !v);
        }
      }}
    >
      <div className="flex items-start justify-between gap-2">
        <h3 className="text-base leading-snug font-semibold tracking-tight">{feature.title}</h3>
        <div className="flex shrink-0 items-center gap-2">
          {feature.journey ? (
            <span className="font-data rounded-sm bg-[oklch(0.72_0.17_190_/_0.10)] px-2 py-0.5 text-[10px] tracking-[0.12em] text-[oklch(0.85_0.15_190)] uppercase">
              {feature.journey}
            </span>
          ) : null}
          {hasDescription ? (
            <ChevronDown
              className={`text-muted-foreground size-3.5 transition-transform print:hidden ${open ? "rotate-180" : ""}`}
            />
          ) : null}
        </div>
      </div>
      {feature.subtitle ? (
        <p className="text-foreground/75 -mt-1 text-sm leading-snug">{feature.subtitle}</p>
      ) : null}
      {hasDescription ? (
        <p
          className={`text-foreground/80 text-[13px] leading-relaxed ${open ? "" : "hidden print:block"}`}
        >
          {feature.description}
        </p>
      ) : null}
      <div className="border-border/40 flex items-center gap-2 border-t pt-2">
        <StatusDot
          tone={
            feature.status === "deferred"
              ? "open"
              : (statusToTone(feature.status) as "open" | "active" | "resolved" | "blocked")
          }
          size="sm"
        />
        <span className="font-data text-muted-foreground text-[10px] tracking-[0.18em] uppercase">
          {feature.status}
        </span>
      </div>
    </article>
  );
}

// ─── Sequence-diagram builder for flow_steps ───────────────────────────────
//
// Translates `meetings.flow_steps` (JSON array of MeetingFlowStep) into a
// Mermaid `sequenceDiagram` source. Each step's `integration` becomes a
// participant; consecutive steps form a chain (prev integration → current).
// Step labels become messages; `detail` (when present) becomes a Note over
// the receiving participant.
//
// Reference: PR #16 (fan-servant branch) introduced this pattern for the
// /scope view's user-flows. We reuse the MermaidDiagram component but build
// the chart dynamically from meeting data instead of hand-authoring it.

function buildSequenceChart(steps: MeetingFlowStep[]): string {
  const idByDisplay = new Map<string, string>();
  function actorId(displayRaw: string): string {
    const display = displayRaw.trim() || "System";
    const existing = idByDisplay.get(display);
    if (existing) return existing;
    let base = display.replace(/[^a-zA-Z0-9]/g, "_").replace(/^_+|_+$/g, "");
    if (!base) base = "x";
    let id = base;
    let n = 1;
    const taken = new Set(idByDisplay.values());
    while (taken.has(id)) {
      n++;
      id = `${base}_${n}`;
    }
    idByDisplay.set(display, id);
    return id;
  }

  // Many meeting steps annotate the integration field with composite values
  // like "New B2B PIM + GS1" or "B2B app + Obligo". For the sequence diagram
  // we collapse to the primary (first) system — repeats then merge into the
  // same lane and the diagram stays clean. The secondary system's role is
  // preserved in the message label / Note.
  function primarySystem(integration: string | null): string {
    if (!integration) return "System";
    const first = integration.split(/[+/&,]/)[0]?.trim();
    return first || "System";
  }

  const userId = actorId("User");
  for (const s of steps) {
    actorId(primarySystem(s.integration));
  }

  const lines: string[] = ["sequenceDiagram", "  autonumber"];

  // Mermaid's sequenceDiagram parser is fussy about punctuation in any text
  // token (messages, notes, even display names). Empirically these break:
  //   - newlines / tabs    → collapse to spaces
  //   - `;`                → treated as statement separator in some grammars
  //   - `(` `)` `<` `>`    → confuse the lookahead parser
  //   - `"` `'` `` ` ``    → break the message-text token
  //   - `#`                → comment marker
  function escape(text: string): string {
    return text
      .replace(/[\n\r\t]/g, " ")
      .replace(/\s+/g, " ")
      .replace(/;/g, ",")
      .replace(/[()[\]{}<>]/g, "")
      .replace(/["'`]/g, "")
      .replace(/#/g, "")
      .trim();
  }

  // Note text in meetings is full-prose. The diagram only has room for a
  // headline — take the first sentence (cut at ". "), and cap total length
  // at ~50 chars with an ellipsis. Readers can open the meeting page for
  // the long version.
  function shortenNote(text: string, maxLen = 50): string {
    const cleaned = escape(text);
    const periodIdx = cleaned.indexOf(". ");
    const sentence =
      periodIdx > 0 && periodIdx <= maxLen + 15 ? cleaned.slice(0, periodIdx) : cleaned;
    if (sentence.length <= maxLen) return sentence;
    return sentence.slice(0, maxLen).replace(/\s+\S*$/, "") + "…";
  }

  // Display names go in unquoted — Mermaid renders quotes literally otherwise
  // (see PR #16 reference). Strip any character that would break the parser
  // up to end-of-line.
  for (const [display, id] of idByDisplay) {
    lines.push(`  participant ${id} as ${escape(display)}`);
  }

  let prev = userId;
  for (const s of steps) {
    const target = actorId(primarySystem(s.integration));
    const label = escape(s.label || "");
    lines.push(`  ${prev}->>${target}: ${label}`);
    if (s.detail) {
      lines.push(`  Note over ${target}: ${shortenNote(s.detail)}`);
    }
    prev = target;
  }

  return lines.join("\n");
}

function FlowSequenceDiagram({ steps }: { steps: MeetingFlowStep[] }) {
  const chart = useMemo(() => buildSequenceChart(steps), [steps]);
  return (
    <MermaidDiagram
      chart={chart}
      eyebrow="Sequence diagram"
      title="Flow as a sequence"
      caption={`${steps.length} step${steps.length === 1 ? "" : "s"} · derived from the meeting's flow_steps.`}
      fallbackHeight={Math.max(280, steps.length * 36)}
    />
  );
}

function OpenDecisionBlock({ decision }: { decision: DecisionRow }) {
  return (
    <article className="border-border/40 flex flex-col gap-2 rounded-lg border bg-[oklch(0.85_0.14_80_/_0.04)] p-4 print:break-inside-avoid">
      <div className="flex items-start gap-2">
        <StatusDot tone="amber" size="md" ring />
        <h3 className="text-sm leading-snug font-semibold tracking-tight">{decision.title}</h3>
      </div>
      {decision.alternatives ? (
        <p className="text-foreground/80 text-[12px] leading-relaxed">
          <span className="font-data text-muted-foreground tracking-wider uppercase">Choices:</span>{" "}
          {decision.alternatives}
        </p>
      ) : null}
      {decision.rationale ? (
        <p className="text-muted-foreground text-[11px] leading-relaxed italic">
          {decision.rationale}
        </p>
      ) : null}
    </article>
  );
}

function OpenQuestionBlock({ index, question }: { index: number; question: OpenQuestionRow }) {
  return (
    <li className="border-border/40 flex items-start gap-3 rounded-lg border p-3 print:break-inside-avoid">
      <span className="font-data text-muted-foreground w-8 shrink-0 text-[12px] tabular-nums">
        Q{index}
      </span>
      <p className="flex-1 text-[13px] leading-relaxed">{question.question}</p>
    </li>
  );
}
